import { getCityLocations } from "utils/getCityLocations";
import { MAP_TYPES } from "pages/constants";
import { AREA_TYPE } from "components/audience-map/options";
import type { RecommendedZone } from "./TargetingContext";

// A selectable distribution point (pin) on the H2H map.
export interface H2hPin {
  id: string;
  name: string;
  lat: number;
  lng: number;
  isSelfServe: boolean;
}

// Fetches the city's enabled distribution points (system locations). Custom
// self-serve / campaign locations are part of the deferred "create new location"
// flow, so only the fixed point catalogue is loaded here.
export const fetchH2hPins = async (cityId: number): Promise<H2hPin[]> => {
  const locations = await getCityLocations({ cityId: String(cityId), isSelfServe: false });
  return (locations ?? [])
    .filter((l: any) => typeof l?.latitude === "number" && typeof l?.longitude === "number" && !!l?.name)
    .map((l: any) => ({
      id: String(l.id),
      name: String(l.name),
      lat: l.latitude,
      lng: l.longitude,
      isSelfServe: !!l.isSelfServe,
    }));
};

type LatLng = { lat: number; lng: number };

// A predicate over a recommended zone (polygon or radius circle) built from the
// loaded Google Maps geometry library. Mirrors the legacy containment check
// (geometry.poly.containsLocation / spherical distance) without the turf
// union/mask pipeline — F4 only needs "is this point inside this zone?".
const zoneTester = (zone: RecommendedZone): ((p: LatLng) => boolean) | null => {
  const g = window.google?.maps;
  // The maps namespace appears before its classes finish initializing (async
  // loading) — require the constructors and geometry lib, not just the object.
  if (!g?.LatLng || !g?.Polygon || !g?.geometry) return null;

  const paths = zone.paths as Array<LatLng | [number, number]> | undefined;
  if (zone.type === MAP_TYPES.POLYGON && Array.isArray(paths) && paths.length >= 3) {
    const ring = paths.map((pt) => (Array.isArray(pt) ? { lat: pt[1], lng: pt[0] } : { lat: pt.lat, lng: pt.lng }));
    const polygon = new g.Polygon({ paths: ring });
    return (p) => g.geometry.poly.containsLocation(new g.LatLng(p.lat, p.lng), polygon);
  }

  if (zone.center && typeof zone.radius === "number") {
    const center = new g.LatLng(zone.center.lat, zone.center.lng);
    return (p) => g.geometry.spherical.computeDistanceBetween(new g.LatLng(p.lat, p.lng), center) <= zone.radius!;
  }

  return null;
};

// True when a point sits inside a distribution zone and outside every exclusion
// zone — the same coverage rule as filterPinsInZones, for a single coordinate.
// With no usable distribution zone defined it returns true (nothing to gate on),
// so a missing-zones city never spuriously flags a searched address as out of area.
export const isInsideZones = (point: LatLng, zones: RecommendedZone[]): boolean => {
  const include = zones
    .filter((z) => z.areaType !== AREA_TYPE.EXCLUSION)
    .map(zoneTester)
    .filter(Boolean) as Array<(p: LatLng) => boolean>;
  const exclude = zones
    .filter((z) => z.areaType === AREA_TYPE.EXCLUSION)
    .map(zoneTester)
    .filter(Boolean) as Array<(p: LatLng) => boolean>;

  if (!include.length) return true;
  if (!include.some((test) => test(point))) return false;
  return !exclude.some((test) => test(point));
};

// Keeps only pins that fall inside a distribution zone and outside every
// exclusion zone — matching the legacy "inside coverage" rule. If no usable
// distribution zone exists, returns an empty list (no coverage = no pins).
export const filterPinsInZones = (pins: H2hPin[], zones: RecommendedZone[]): H2hPin[] => {
  const include = zones
    .filter((z) => z.areaType !== AREA_TYPE.EXCLUSION)
    .map(zoneTester)
    .filter(Boolean) as Array<(p: LatLng) => boolean>;
  const exclude = zones
    .filter((z) => z.areaType === AREA_TYPE.EXCLUSION)
    .map(zoneTester)
    .filter(Boolean) as Array<(p: LatLng) => boolean>;

  if (!include.length) return [];

  return pins.filter((pin) => {
    const inside = include.some((test) => test(pin));
    if (!inside) return false;
    return !exclude.some((test) => test(pin));
  });
};
